import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useLocation } from 'react-router';
import { useAppSelector } from 'app/hooks';
import { useDrawer } from 'components/app/DrawerContext';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  manageOperationsPercentage,
  selectLoading as selectConfigLoading
} from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';
import {
  getLandingData,
  purchaseUnits,
  resetUnitsPurchaseStatus,
  selectError,
  selectLandingData,
  selectLandingDataStatus,
  selectLastUnitsPurchase,
  selectLoading,
  selectUnitsPurchaseStatus,
  type PurchaseUnitsPayload
} from 'slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { fetchRecords, type ListRecordsPayload } from 'slices/incomeExpenses/IncomeExpensesSlice';
import { RootState } from 'store';
import {
  type UnitClass,
  type SubscriptionFormData,
  type OperationalFees,
  type SnackbarState,
  validationSchema
} from './UnitPurchaseTypes';
import {
  getGroupId,
  getLoggedInUser,
  fetchAvatarUrl,
  processLandingData,
  checkCapacitySufficient,
  getCapacityAfterPurchase,
  getDataSourceStatus
} from './UnitPurchaseUtils';

export const useUnitPurchaseLogic = () => {
  const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fundManagementAdminRight } = useDrawer();

  const rawMarketplaceData = location.state?.fromMarketplace ? location.state.sellingRequest : null;
  const hasValidMarketplaceRequest =
    !!rawMarketplaceData &&
    !!rawMarketplaceData.requestId &&
    !!rawMarketplaceData.date_str &&
    Number(rawMarketplaceData.sellUnits) > 0 &&
    Number(rawMarketplaceData.unitPrice) > 0;
  const marketplaceData = hasValidMarketplaceRequest ? rawMarketplaceData : null;

  // Redux state
  const purchaseStatus = useSelector(selectUnitsPurchaseStatus);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const lastTransaction = useSelector(selectLastUnitsPurchase);
  const landingData = useSelector(selectLandingData);
  const landingDataStatus = useSelector(selectLandingDataStatus);
  const configLoading = useSelector(selectConfigLoading);
  const capacitySummary = useSelector((state: RootState) => state.incomeExpenses?.summary);
  const capacityLoading = useSelector((state: RootState) => state.incomeExpenses?.loading);
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector((state) => {
    const title = state.serviceSelected.selectedServiceTitle || 'fundManagementInvestment';
    return title.replace(/\s+/g, '').replace(/^[A-Z]/, (m) => m.toLowerCase());
  });

  // Local UI/logic state
  const [isAdmin, setIsAdmin] = useState(fundManagementAdminRight);
  const loggedInUser = useMemo(() => getLoggedInUser(), []);
  const [selectedUnitClass, setSelectedUnitClass] = useState<UnitClass | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Certificate modal state
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [certificatePayload, setCertificatePayload] = useState<any>(null);

  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string | null }>({});
  const [loggedUserAvatarUrl, setLoggedUserAvatarUrl] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [operationalFees, setOperationalFees] = useState<OperationalFees>({
    incomeUnits: 0,
    growthUnits: 0
  });
  const [feeLoadingStatus, setFeeLoadingStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  // ** Hold the subscriptionType for redirect **
  const lastSubmittedSubscriptionType = useRef<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset
  } = useForm<SubscriptionFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      investorName: loggedInUser.name,
      investorsEmailId: loggedInUser.email,
      investorAvatar: loggedInUser.avatar,
      calculationType: marketplaceData ? 'units' : 'amount',
      purchaseUnits: marketplaceData ? toNumber(marketplaceData.sellUnits) : 0,
      totalInvestmentAmt: marketplaceData
        ? toNumber(marketplaceData.sellUnits) * toNumber(marketplaceData.unitPrice)
        : 0,
      subscriptionType: marketplaceData ? marketplaceData.subscriptionType : 'incomeUnits'
    },
    mode: 'onChange'
  });

  const watchedValues = watch();
  const unitClasses = useMemo(
    () => processLandingData(landingData, landingDataStatus),
    [landingData, landingDataStatus]
  );
  const operationPercentage = operationalFees[watchedValues.subscriptionType] || 0;

  const operationCharge = useMemo(() => {
    const baseAmount = toNumber(watchedValues.totalInvestmentAmt);
    return parseFloat((baseAmount * (operationPercentage / 100)).toFixed(2));
  }, [watchedValues.totalInvestmentAmt, operationPercentage]);

  const totalWithFee = useMemo(() => {
    const baseAmount = toNumber(watchedValues.totalInvestmentAmt);
    return parseFloat((baseAmount + operationCharge).toFixed(2));
  }, [watchedValues.totalInvestmentAmt, operationCharge]);

  useEffect(() => {
    if (location.state?.fromMarketplace && !hasValidMarketplaceRequest) {
      setSnackbar({
        open: true,
        message:
          'Marketplace sell request data is missing or expired. Please choose a unit class and submit a normal purchase.',
        severity: 'error'
      });
    }
  }, [location.state, hasValidMarketplaceRequest]);

  const getRemainingCapacity = (): number => {
    if (!capacitySummary || typeof capacitySummary.my_capacity !== 'number') return 0;
    return capacitySummary.my_capacity;
  };

  const getTotalCost = () => toNumber(watchedValues.totalInvestmentAmt);

  // Effects
  useEffect(() => {
    if (marketplaceData) {
      const sellUnits = toNumber(marketplaceData.sellUnits);
      const unitPrice = toNumber(marketplaceData.unitPrice);
      setValue('purchaseUnits', sellUnits, { shouldValidate: true, shouldDirty: true });
      setValue('totalInvestmentAmt', sellUnits * unitPrice, { shouldValidate: true, shouldDirty: true });
      setValue('subscriptionType', marketplaceData.subscriptionType, { shouldValidate: true, shouldDirty: true });
      setValue('calculationType', 'units', { shouldValidate: true, shouldDirty: true });
      setShowForm(true);

      const marketplaceUnitClass: UnitClass = {
        id: `marketplace-${marketplaceData.subscriptionType}`,
        name: marketplaceData.subscriptionType === 'incomeUnits' ? 'Income Units' : 'Growth Units',
        type: marketplaceData.subscriptionType === 'incomeUnits' ? 'Income' : 'Growth',
        unitPrice: unitPrice,
        totalUnits: sellUnits,
        totalNAV: toNumber(marketplaceData.totalSellAmount),
        description:
          marketplaceData.subscriptionType === 'incomeUnits'
            ? 'Conservative investment focused on regular income generation through quarterly distributions'
            : 'Growth-focused investment for long-term capital appreciation with higher risk tolerance',
        features:
          marketplaceData.subscriptionType === 'incomeUnits'
            ? [
                'Regular quarterly distributions',
                'Lower volatility',
                'Stable income stream',
                'Conservative investment approach'
              ]
            : [
                'Capital growth focus',
                'Higher growth potential',
                'Reinvested earnings',
                'Long-term wealth building'
              ],
        riskLevel:
          marketplaceData.subscriptionType === 'incomeUnits' ? 'Conservative' : 'Moderate to High',
        minimumInvestment: sellUnits * unitPrice,
        subscriptionType: marketplaceData.subscriptionType
      };

      setSelectedUnitClass(marketplaceUnitClass);
      trigger();
    }
  }, [marketplaceData, setValue, trigger]);

  useEffect(() => {
    const groupId = getGroupId();
    if (groupId) {
      dispatch(getLandingData({ groupId, serviceId: 'fundManagementInvestment', isAdmin }) as any);
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    const projectId = selectedProjectId || getGroupId();
    if (projectId && selectedServiceTitle) {
      const payload: ListRecordsPayload = {
        group_id: projectId,
        service_id: 'Budgeting Tools',
        transaction_type: 'all',
        current_year: new Date().getFullYear().toString(),
        time_filter: null,
        page_size: 10,
        continuation_token: null
      };
      dispatch(fetchRecords(payload) as any);
    }
  }, [dispatch, selectedProjectId, selectedServiceTitle]);

  useEffect(() => {
    const loadAvatar = async () => {
      if (loggedInUser.avatar) {
        const url = await fetchAvatarUrl(loggedInUser.avatar);
        setLoggedUserAvatarUrl(url);
      }
    };
    loadAvatar();
    return () => {
      if (loggedUserAvatarUrl) URL.revokeObjectURL(loggedUserAvatarUrl);
    };
  }, [loggedInUser.avatar]);

  useEffect(() => {
    const loadOperationalPercentage = async () => {
      try {
        const projectId = selectedProjectId || getGroupId();
        setFeeLoadingStatus('loading');
        const result = await dispatch(
          manageOperationsPercentage({
            action: 'get',
            groupId: projectId,
            serviceId: 'fundManagementInvestment',
            unitType: watchedValues.subscriptionType,
            perType: 'buy'
          }) as any
        );
        if (result.payload?.response?.data?.percentage !== undefined) {
          const percentage = result.payload.response.data.percentage;
          setOperationalFees((prev) => ({
            ...prev,
            [watchedValues.subscriptionType]: percentage
          }));
          setFeeLoadingStatus('success');
        } else {
          setFeeLoadingStatus('error');
        }
      } catch {
        setFeeLoadingStatus('error');
        setOperationalFees((prev) => ({
          ...prev,
          [watchedValues.subscriptionType]: 0
        }));
      }
    };
    if (watchedValues.subscriptionType && showForm) {
      loadOperationalPercentage();
    }
  }, [watchedValues.subscriptionType, showForm, dispatch, selectedProjectId]);

  useEffect(() => {
    if (purchaseStatus === 'succeeded' && lastTransaction) {
      setSnackbar({
        open: true,
        message: 'Purchase request submitted successfully!',
        severity: 'success'
      });
      setTimeout(() => {
        setShowForm(false);
        setSelectedUnitClass(null);
        reset({
          investorName: loggedInUser.name,
          investorsEmailId: loggedInUser.email,
          investorAvatar: loggedInUser.avatar,
          calculationType: 'amount',
          purchaseUnits: 0,
          totalInvestmentAmt: 0,
          subscriptionType: 'incomeUnits'
        });
        dispatch(resetUnitsPurchaseStatus());
      }, 3000);
    } else if (purchaseStatus === 'failed' && error) {
      setSnackbar({
        open: true,
        message: error,
        severity: 'error'
      });
    }
  }, [purchaseStatus, lastTransaction, error, reset, dispatch, loggedInUser]);

  // UI event handlers
  const handleUnitClassSelect = (unitClass: UnitClass) => {
    setSelectedUnitClass(unitClass);
    const minInvestment = unitClass.minimumInvestment;
    const calculatedUnits = minInvestment / unitClass.unitPrice;
    setValue('subscriptionType', unitClass.subscriptionType, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue('totalInvestmentAmt', minInvestment, { shouldValidate: true, shouldDirty: true });
    setValue('purchaseUnits', calculatedUnits, { shouldValidate: true, shouldDirty: true });
    setValue('calculationType', 'amount', { shouldValidate: true, shouldDirty: true });
    setShowForm(true);
    trigger();
  };

  const handleSelectSelf = () => {
    setValue('investorName', loggedInUser.name, { shouldValidate: true, shouldDirty: true });
    setValue('investorsEmailId', loggedInUser.email, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue('investorAvatar', loggedInUser.avatar, { shouldValidate: true, shouldDirty: true });
    trigger();
  };

  const handleCalculationTypeChange = (type: 'amount' | 'units') => {
    if (!selectedUnitClass) return;
    setValue('calculationType', type, { shouldValidate: true, shouldDirty: true });
    if (type === 'amount') {
      const currentAmount =
        toNumber(watchedValues.totalInvestmentAmt) || selectedUnitClass.minimumInvestment;
      const calculatedUnits = currentAmount / selectedUnitClass.unitPrice;
      setValue('purchaseUnits', calculatedUnits, { shouldValidate: true, shouldDirty: true });
    } else {
      const currentUnits =
        toNumber(watchedValues.purchaseUnits) ||
        selectedUnitClass.minimumInvestment / selectedUnitClass.unitPrice;
      const calculatedAmount = currentUnits * selectedUnitClass.unitPrice;
      setValue('totalInvestmentAmt', calculatedAmount, { shouldValidate: true, shouldDirty: true });
    }
    trigger();
  };

  const handleAmountChange = (amount: number) => {
    if (!selectedUnitClass) return;
    const calculatedUnits = amount / selectedUnitClass.unitPrice;
    setValue('purchaseUnits', calculatedUnits, { shouldValidate: true, shouldDirty: true });
    setValue('totalInvestmentAmt', amount, { shouldValidate: true, shouldDirty: true });
  };

  const handleUnitsChange = (units: number) => {
    if (!selectedUnitClass) return;
    const calculatedAmount = units * selectedUnitClass.unitPrice;
    setValue('totalInvestmentAmt', calculatedAmount, { shouldValidate: true, shouldDirty: true });
    setValue('purchaseUnits', units, { shouldValidate: true, shouldDirty: true });
  };

console.log('marketplaceData', marketplaceData);

  const onSubmit = async (data: SubscriptionFormData) => {
    const resolvedUnitClass =
      selectedUnitClass ||
      unitClasses.find((item) => item.subscriptionType === data.subscriptionType) ||
      null;

    if (!resolvedUnitClass) {
      setSnackbar({
        open: true,
        message: 'Please select a unit class before purchasing.',
        severity: 'error'
      });
      return;
    }

    const projectId = selectedProjectId || getGroupId();
    const requestedAmount = totalWithFee;
    const availableCapacity = getRemainingCapacity();
    const hasCapacityData =
      !!capacitySummary && typeof (capacitySummary as any).my_capacity === 'number';

    if (hasCapacityData && requestedAmount > availableCapacity) {
      const shortfall = requestedAmount - availableCapacity;
      setSnackbar({
        open: true,
        message: `Insufficient Funds! You are requesting $${requestedAmount.toFixed(
          2
        )} but only have $${availableCapacity.toFixed(2)} available. You need $${shortfall.toFixed(
          2
        )} more capacity.`,
        severity: 'error'
      });
      return;
    }

    const payload: PurchaseUnitsPayload = {
      groupId: projectId,
      serviceId: 'fundManagementInvestment',
      investorsEmailId: data.investorsEmailId || loggedInUser.email || '',
      investorName: data.investorName || loggedInUser.name || 'Investor',
      investorAvatar: data.investorAvatar || loggedInUser.avatar || '',
      purchaseUnits: toNumber(data.purchaseUnits).toFixed(2),
      totalInvestmentAmt: toNumber(data.totalInvestmentAmt).toFixed(2),
      unitPrice: resolvedUnitClass.unitPrice.toFixed(2),
      operationCharge: operationCharge.toFixed(2),
      subscriptionType: data.subscriptionType
    };

    if (marketplaceData) {
      (payload as any).requestId = marketplaceData?.requestId;
      (payload as any).fromMarketplace = true;
      (payload as any).date_str = marketplaceData?.date_str;
    }

    // Save subscriptionType for after reset
    lastSubmittedSubscriptionType.current = data.subscriptionType;

    try {
      console.log('Submitting purchase payload', payload);
      const result = await dispatch(purchaseUnits(payload) as any);

      if (purchaseUnits.fulfilled.match(result)) {
        setSnackbar({
          open: true,
          message: marketplaceData
            ? `Purchase request submitted successfully! Buying ${data.purchaseUnits} units from ${marketplaceData.sellerName}.`
            : 'Purchase request submitted successfully!',
          severity: 'success'
        });

        setCertificatePayload({
          mode: 'preview',
          previewData: {
            investorName: data.investorName,
            investorEmail: data.investorsEmailId,
            units: data.purchaseUnits,
            unitPrice: resolvedUnitClass.unitPrice,
            unitType: data.subscriptionType,
            transactionDate: new Date().toISOString()
          }
        });
        setShowCertificateModal(true);

        if (projectId && selectedServiceTitle) {
          const refreshPayload: ListRecordsPayload = {
            group_id: projectId,
            service_id: 'Budgeting Tools',
            transaction_type: 'all',
            current_year: '2025',
            time_filter: null,
            page_size: 10,
            continuation_token: null
          };
          dispatch(fetchRecords(refreshPayload) as any);
        }
      } else {
        setSnackbar({
          open: true,
          message: (result.error as any)?.message || 'Purchase submission failed',
          severity: 'error'
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: 'Purchase submission failed. Please try again.',
        severity: 'error'
      });
    }
  };

  // Close certificate modal and route to approval (uses ref for type)
  const handleCertificateClose = () => {
    const redirectTo = lastSubmittedSubscriptionType.current || watchedValues.subscriptionType;
    setShowCertificateModal(false);
    navigate('/fundManagementInvestment/request', {
      state: {
        from: marketplaceData ? 'marketplace' : 'unitPurchase',
        to: redirectTo,
        isAdmin: isAdmin
      }
    });
    setShowForm(false);
    setSelectedUnitClass(null);
    reset({
      investorName: loggedInUser.name,
      investorsEmailId: loggedInUser.email,
      investorAvatar: loggedInUser.avatar,
      calculationType: 'amount',
      purchaseUnits: 0,
      totalInvestmentAmt: 0,
      subscriptionType: 'incomeUnits'
    });
    dispatch(resetUnitsPurchaseStatus());
  };

  return {
    unitClasses,
    selectedUnitClass,
    showForm,
    loading,
    landingDataStatus,
    capacitySummary,
    capacityLoading,
    loggedInUser,
    loggedUserAvatarUrl,
    snackbar,
    operationalFees,
    feeLoadingStatus,
    isMobile,
    marketplaceData,
    control,
    handleSubmit,
    watchedValues,
    errors,
    isValid,
    setValue,
    operationPercentage,
    operationCharge,
    totalWithFee,
    setShowForm,
    setSnackbar,
    handleUnitClassSelect,
    handleSelectSelf,
    handleCalculationTypeChange,
    handleAmountChange,
    handleUnitsChange,
    onSubmit,
    checkCapacitySufficient: (amount: number) =>
      checkCapacitySufficient(amount, getRemainingCapacity()),
    getRemainingCapacity,
    getCapacityAfterPurchase: (amount: number) =>
      getCapacityAfterPurchase(amount, getRemainingCapacity()),
    getTotalCost,
    getDataSourceStatus: () => getDataSourceStatus(loading, landingDataStatus, unitClasses),
    // Certificate Modal Control Props
    showCertificateModal,
    certificatePayload,
    handleCertificateClose
  };
};
